import { computed, createApp, onMounted, ref } from 'vue';

import * as AnalyticsData from '~/analytics/analytics-data';
import { GEOFENCING_SHOPS } from '~/constants/geofencing';
import { MARKETING_USER_PREFERENCES, TRACK_COMMUNICATION_LINKS } from '~/constants/rudderstack';
import * as DataDogLogs from '~/lib/datadog/datadog';
import * as Iterable from '~/lib/iterable/iterable';
import * as RudderstackClient from '~/lib/rudderstack/rudderstack-client';
import * as RudderstackUtils from '~/lib/rudderstack/rudderstack-utils';
import * as CustomerUtils from '~/utils/customer';
import * as ValidatorUtils from '~/utils/validator';

import { useModal } from '../composables/use-modal';
import { useViewportSize } from '../composables/use-viewport-size';
import { BaseSection } from './base-section';

const SECTION_NAME = 'footer';

/**
 * Footer Section
 *
 * This section displays a footer with a grid of links and social media icons.
 * The section uses Vue.js to handle the menu state and toggle functionality.
 *
 * @extends BaseSection
 * @constructor
 * @param {HTMLElement} container - The container element for the section
 */
class Footer extends BaseSection {
  constructor() {
    super(`[data-section-type="${SECTION_NAME}"]`);
  }

  init(instanceData) {
    const container = instanceData.container;
    const menuElements = container.querySelectorAll(`[data-${SECTION_NAME}-menu]`);

    if (!container) {
      DataDogLogs.error(
        '[Allbirds section] - Footer - init - Container not found',
        {},
        new Error('Container not found'),
      );
      return;
    }

    createApp({
      name: 'FooterSection',
      setup() {
        const menuStates = ref({});
        const { closeModal, isModalOpen, modalStates, openModal } = useModal();
        const { isMobile } = useViewportSize();
        const emailValue = ref('');
        const emailProvided = ref(false);
        const showInvalidEmailMessage = ref(false);
        const geoFencingOptions = computed(() => {
          return Object.values(GEOFENCING_SHOPS).map((shop) => ({
            label: shop.id,
            value: shop.url,
          }));
        });

        onMounted(() => {
          menuElements.forEach((menu) => {
            const menuId = menu.getAttribute(`data-${SECTION_NAME}-menu`);
            if (menuId) {
              menuStates.value[menuId] = false;
            }
          });

          // Hook the analytics events
          AnalyticsData.hookFooterLinkClicked({ sectionName: SECTION_NAME });
        });

        /**
         * Toggle the visibility of a footer menu
         * @param {string} menuId - The ID of the menu to toggle
         */
        const toggleMenu = (menuId) => {
          // Ensure the menu ID exists in our state
          if (typeof menuStates.value[menuId] !== 'undefined') {
            menuStates.value[menuId] = !menuStates.value[menuId];
          }
        };

        /**
         * Check if a menu is open
         * @param {string} menuId - The ID of the menu to check
         * @returns {boolean} - Whether the menu is open
         */
        const isMenuOpen = (menuId) => {
          return menuStates.value[menuId] === true;
        };

        const handleSignUpClick = () => {
          const email = emailValue.value.trim();
          const placementSource = MARKETING_USER_PREFERENCES.PLACEMENT.FOOTER;
          const iterablePayload = Iterable.buildPayload({
            email,
            isMobile: isMobile.value,
            placementSource,
          });

          // Validate email
          if (email == '' || !ValidatorUtils.isEmail(email)) {
            showInvalidEmailMessage.value = true;

            return;
          }

          // Update User cookies state
          CustomerUtils.identifyActionEmail(email, CustomerUtils.getIdentifiedUserState());

          // Identify user
          RudderstackClient.identifyUser({
            additionalTraits: RudderstackUtils.getUserMarketingEmailTraits({
              subscriptionSource: placementSource,
              subscriptionStatus: true,
            }),
            email,
          });

          // Update user marketing preferences
          RudderstackClient.userMarketingPreference({
            messageMedium: MARKETING_USER_PREFERENCES.MEDIUM.EMAIL,
            messageType: MARKETING_USER_PREFERENCES.TYPE.ALL,
            optInStatus: true,
            placement: placementSource,
          });

          // Push data to Iterable
          Iterable.pushSignUp(iterablePayload);

          // Update vue state
          emailValue.value = '';
          emailProvided.value = true;
          showInvalidEmailMessage.value = false;
        };

        const handleSocialIconClick = (socialHeading, socialIconUrl, socialIconName) => {
          // Used navigation delay workaround for the Analytics, because 'Query String API' can not be used when the destination is an external domain
          RudderstackClient.navigationDelayForIphoneAndSafari({
            analyticsFn: () => {
              RudderstackClient.footerLinkClicked({
                footerLinkCategory: socialHeading,
                footerLinkName: `${TRACK_COMMUNICATION_LINKS.SOCIAL_MEDIA_LINKS_TRACKING_NAME_PREFIX}${socialIconName}`,
              });
            },
            openInNewTab: true, // Open the social media link in a new tab
            urlToNavigate: socialIconUrl,
          });
        };

        const handleComunicationLinkClick = (linkUrl, linkName, linkCategory) => {
          // If the communication link is Chat with Us!, then open the chat bubble, Shopify menu for footer should contain the "open-chat" string in the link
          // Otherwise, treat it as a regular communication link SMS, Tel, Email, etc.
          if (linkUrl.includes(TRACK_COMMUNICATION_LINKS.OPEN_CHAT_KEYWORD)) {
            RudderstackClient.footerLinkClicked({
              footerLinkCategory: linkCategory,
              footerLinkName: linkName,
            });

            // Open the chat bubble using the Gladly API
            window?.Gladly?.show();
          } else {
            // Used navigation delay workaround for the Analytics, because 'Query String API' can not be used when the destination is an external domain
            RudderstackClient.navigationDelayForIphoneAndSafari({
              analyticsFn: () => {
                RudderstackClient.footerLinkClicked({
                  footerLinkCategory: linkCategory,
                  footerLinkName: linkName,
                });
              },
              openInNewTab: false,
              urlToNavigate: linkUrl,
            });
          }
        };

        const handleCountryChange = (event) => {
          window.location.href = event.target.value;
        };

        return {
          closeModal,
          emailProvided,
          emailValue,
          geoFencingOptions,
          handleComunicationLinkClick,
          handleCountryChange,
          handleSignUpClick,
          handleSocialIconClick,
          isMenuOpen,
          isMobile,
          isModalOpen,
          menuStates,
          modalStates,
          openModal,
          showInvalidEmailMessage,
          toggleMenu,
        };
      },
    }).mount(container);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new Footer();
});
